// グラフX軸の粒度（日/月/年）を、表示期間に応じて自動判定・整形するユーティリティ。
// バックエンドは ISO 日付（YYYY-MM-DD）を labels として返し、表示ラベルの整形はここに集約する。

export type AxisGranularity = "day" | "month" | "year"

interface DateParts {
  year: number
  month: number // 1-12
  day: number
}

/** "YYYY-MM-DD"（先頭が同形式のISO文字列）をタイムゾーンに依存せず分解する */
const parseIsoDate = (iso: string): DateParts => {
  const [datePart] = iso.split("T")
  const [year, month, day] = datePart.split("-").map(Number)
  return { year, month, day }
}

const DAY_MS = 24 * 60 * 60 * 1000

/** ISO日付配列の期間（先頭〜末尾の日数）から軸の粒度を判定する */
export const pickGranularity = (isoDates: string[]): AxisGranularity => {
  if (isoDates.length < 2) return "day"

  const first = parseIsoDate(isoDates[0])
  const last  = parseIsoDate(isoDates[isoDates.length - 1])
  const firstMs = Date.UTC(first.year, first.month - 1, first.day)
  const lastMs  = Date.UTC(last.year, last.month - 1, last.day)
  const days = Math.round((lastMs - firstMs) / DAY_MS)

  if (days <= 31) return "day"    // 〜1ヶ月: 日次（M/D）
  if (days <= 731) return "month" // 〜約2年: 月次（YYYY/M）
  return "year"                   // 約2年超: 年次（YYYY）
}

const pad2 = (n: number): string => String(n).padStart(2, "0")

/** 粒度に応じた軸ラベル（日: dd, 月: yyyy/mm, 年: yyyy）。日付フルはツールチップで補完する */
export const formatAxisLabel = (iso: string, granularity: AxisGranularity): string => {
  const { year, month, day } = parseIsoDate(iso)
  if (granularity === "year") return `${year}`         // yyyy   例: 2026
  if (granularity === "month") return `${year}/${pad2(month)}` // yyyy/mm 例: 2026/07
  return pad2(day)                                     // dd     例: 17
}

/** ツールチップ用の日付フル表記（YYYY/M/D） */
export const formatTooltipDate = (iso: string): string => {
  const { year, month, day } = parseIsoDate(iso)
  return `${year}/${month}/${day}`
}

/**
 * 各データ点に対応する軸ラベル配列。
 * 月/年粒度では「単位が変わる最初の点」だけラベルを出し、それ以外は空文字にして重複表示を抑える。
 * 日粒度は全点を M/D に整形し、間引き（表示数調整）は chart.js の autoSkip に委ねる。
 */
export const buildAxisTickLabels = (isoDates: string[], granularity: AxisGranularity): string[] =>
  isoDates.map((iso, i) => {
    const label = formatAxisLabel(iso, granularity)
    if (granularity === "day" || i === 0) return label
    return formatAxisLabel(isoDates[i - 1], granularity) === label ? "" : label
  })
